import { useEffect, useMemo, useState } from "react";
import { apiFetch } from "../../lib/api";
import { CodexTerminalFrame } from "../ui/CodexTerminalFrame";

type LogRecord = {
  id: string;
  workspace: string;
  kind: string;
  scope: string;
  message: string;
  detail: unknown;
  recorded_at: string;
};

type AnalyzerPayload = {
  answer?: string;
  matches?: LogRecord[];
  summary?: {
    total?: number;
    server_requests?: number;
    client_events?: number;
    runtime_signals?: number;
    last_recorded_at?: string;
  };
  autopilot_runtime?: { state?: string };
  arbiter_runtime?: { state?: string };
};

function formatTime(value?: string) {
  return value ? new Date(value).toLocaleString() : "n/a";
}

export function AnalyzerWorkbench() {
  const [summary, setSummary] = useState<NonNullable<AnalyzerPayload["summary"]>>({});
  const [results, setResults] = useState<LogRecord[]>([]);
  const [query, setQuery] = useState("");
  const [question, setQuestion] = useState("What is stuck right now?");
  const [answer, setAnswer] = useState("Ask the analyzer about drift, loops, or stuck runtime behavior.");
  const [runtimeState, setRuntimeState] = useState<{ autopilot?: string; arbiter?: string }>({});
  const [loading, setLoading] = useState(false);

  async function refresh() {
    const summaryRes = await apiFetch("/api/logs/summary");
    const summaryData = await summaryRes.json();
    setSummary(summaryData);

    const searchRes = await apiFetch("/api/logs/search?limit=20");
    const searchData = await searchRes.json();
    setResults(searchData.results || []);
  }

  useEffect(() => {
    void refresh();
  }, []);

  const signalTone = useMemo(() => {
    const total = summary.total || 0;
    if (total > 250) return "#fca5a5";
    if (total > 80) return "#fde68a";
    return "#c4d4ea";
  }, [summary.total]);

  async function runSearch() {
    setLoading(true);
    try {
      const res = await apiFetch(`/api/logs/search?q=${encodeURIComponent(query)}&limit=40`);
      const data = await res.json();
      setResults(data.results || []);
    } finally {
      setLoading(false);
    }
  }

  async function askAnalyzer() {
    setLoading(true);
    try {
      const res = await apiFetch("/api/logs/ask", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ question }),
      });
      const data: AnalyzerPayload = await res.json();
      setAnswer(data.answer || answer);
      setResults(data.matches || []);
      setSummary(data.summary || {});
      setRuntimeState({
        autopilot: data.autopilot_runtime?.state,
        arbiter: data.arbiter_runtime?.state,
      });
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{ height: "100%", minHeight: 0 }}>
      <CodexTerminalFrame title="Analyzer" prompt="workspace signal ledger">
        <div style={{ display: "grid", gridTemplateRows: "auto auto minmax(0, 1fr)", gap: 12, height: "100%", minHeight: 0 }}>
          <section style={{ display: "grid", gridTemplateColumns: "repeat(6, minmax(0, 1fr))", gap: 12 }}>
            {[
              ["events", String(summary.total || 0)],
              ["server", String(summary.server_requests || 0)],
              ["client", String(summary.client_events || 0)],
              ["runtime", String(summary.runtime_signals || 0)],
              ["autopilot", runtimeState.autopilot || "unknown"],
              ["arbiter", runtimeState.arbiter || "unknown"],
            ].map(([label, value]) => (
              <div key={label} style={{ border: "1px solid rgba(255,255,255,0.08)", borderRadius: 14, padding: "12px 14px", background: "rgba(7,10,16,0.84)" }}>
                <div style={{ color: "#7f8aa3", font: "700 10px/1.2 var(--ot-font-mono)", letterSpacing: "0.08em", textTransform: "uppercase" }}>{label}</div>
                <div style={{ marginTop: 8, color: "#eef4ff", font: "700 22px/1.1 var(--ot-font-mono)" }}>{value}</div>
              </div>
            ))}
          </section>

          <section style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gap: 12 }}>
            <div style={{ border: "1px solid rgba(255,255,255,0.08)", borderRadius: 16, padding: 14, background: "rgba(7,10,16,0.84)" }}>
              <div style={{ color: "#eef4ff", font: "700 12px/1 var(--ot-font-mono)", textTransform: "uppercase", letterSpacing: "0.08em" }}>search logs</div>
              <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                <input
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") void runSearch();
                  }}
                  placeholder="route, blocked, autopilot, browser..."
                  style={{ flex: 1, minWidth: 0, borderRadius: 999, border: "1px solid rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.03)", color: "#eef4ff", padding: "10px 14px", font: "12px/1.4 var(--ot-font-mono)" }}
                />
                <button type="button" onClick={() => void runSearch()} disabled={loading} style={{ borderRadius: 999, border: "1px solid rgba(125,211,252,0.18)", background: "rgba(79,143,255,0.12)", color: "#dff4ff", padding: "10px 14px", font: "700 11px/1 var(--ot-font-mono)", textTransform: "uppercase" }}>
                  find
                </button>
              </div>
            </div>

            <div style={{ border: "1px solid rgba(255,255,255,0.08)", borderRadius: 16, padding: 14, background: "rgba(7,10,16,0.84)" }}>
              <div style={{ color: "#eef4ff", font: "700 12px/1 var(--ot-font-mono)", textTransform: "uppercase", letterSpacing: "0.08em" }}>ask analyzer</div>
              <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                <input
                  value={question}
                  onChange={(event) => setQuestion(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") void askAnalyzer();
                  }}
                  placeholder="what is stuck?"
                  style={{ flex: 1, minWidth: 0, borderRadius: 999, border: "1px solid rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.03)", color: "#eef4ff", padding: "10px 14px", font: "12px/1.4 var(--ot-font-mono)" }}
                />
                <button type="button" onClick={() => void askAnalyzer()} disabled={loading} style={{ borderRadius: 999, border: "1px solid rgba(52,211,153,0.18)", background: "rgba(52,211,153,0.12)", color: "#d8ffee", padding: "10px 14px", font: "700 11px/1 var(--ot-font-mono)", textTransform: "uppercase" }}>
                  ask
                </button>
              </div>
              <p style={{ margin: "12px 0 0", color: signalTone, font: "12px/1.55 var(--ot-font-mono)" }}>{answer}</p>
            </div>
          </section>

          <section style={{ minHeight: 0, overflow: "auto", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 20, background: "linear-gradient(180deg, rgba(7,10,16,0.98), rgba(4,5,10,0.98))", padding: 14 }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, marginBottom: 12 }}>
              <div style={{ color: "#eef4ff", font: "700 12px/1 var(--ot-font-mono)", textTransform: "uppercase", letterSpacing: "0.08em" }}>workspace ledger</div>
              <div style={{ color: "#7f8aa3", font: "11px/1 var(--ot-font-mono)", textTransform: "uppercase" }}>last event {formatTime(summary.last_recorded_at)}</div>
            </div>
            <div style={{ display: "grid", gap: 10 }}>
              {results.map((record) => (
                <article key={record.id} style={{ border: "1px solid rgba(255,255,255,0.05)", borderRadius: 14, padding: 12, background: "rgba(255,255,255,0.02)" }}>
                  <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                    <span style={{ color: "#7dd3fc", font: "700 10px/1 var(--ot-font-mono)", textTransform: "uppercase" }}>{record.kind}</span>
                    <span style={{ color: "#a6b6ca", font: "11px/1 var(--ot-font-mono)" }}>{record.scope}</span>
                    <span style={{ marginLeft: "auto", color: "#7f8aa3", font: "11px/1 var(--ot-font-mono)" }}>{formatTime(record.recorded_at)}</span>
                  </div>
                  <div style={{ marginTop: 8, color: "#eef4ff", font: "13px/1.5 var(--ot-font-mono)" }}>{record.message}</div>
                  <pre style={{ margin: "8px 0 0", color: "#91a3bc", font: "11px/1.5 var(--ot-font-mono)", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                    {JSON.stringify(record.detail, null, 2)}
                  </pre>
                </article>
              ))}
              {results.length === 0 ? (
                <div style={{ color: "#7f8aa3", font: "12px/1.6 var(--ot-font-mono)" }}>No matching events yet. Use the desktop, then search or ask about the resulting traffic and runtime signals.</div>
              ) : null}
            </div>
          </section>
        </div>
      </CodexTerminalFrame>
    </div>
  );
}
